import styled, { keyframes } from "styled-components";
import { colors, font, radius, shadow } from "../../styles/theme/tokens";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const iconPop = keyframes`
  0% { transform: scale(0.5) rotate(-12deg); opacity: 0; }
  70% { transform: scale(1.08) rotate(4deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to { opacity: 1; transform: translateX(0); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(30, 27, 58, 0.5);
  backdrop-filter: blur(3px);
  animation: ${fadeIn} 0.2s ease-out;
`;

export const Modal = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 32px);
  border-radius: ${radius.xl};
  overflow: hidden;
  font-family: ${font.text};
  color: ${colors.ink};
  background: ${colors.surface};
  box-shadow: ${shadow.raised};
  animation: ${popIn} 0.3s ease-out;
`;

export const Close = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  color: #FFFFFF;
  background: rgba(255, 255, 255, 0.16);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

export const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 30px 24px 24px;
  text-align: center;
  color: #FFFFFF;
  background:
    radial-gradient(90% 120% at 50% 0%, rgba(244, 182, 63, 0.4) 0%, rgba(244, 182, 63, 0) 55%),
    linear-gradient(160deg, ${colors.brandDark} 0%, ${colors.brandDeep} 70%, ${colors.brand} 100%);
`;

export const HeroIcon = styled.div`
  display: grid;
  place-items: center;
  width: 76px;
  height: 76px;
  border-radius: 24px;
  color: ${colors.brandDark};
  background: ${colors.amber};
  box-shadow: 0 8px 24px rgba(244, 182, 63, 0.45);
  animation: ${iconPop} 0.5s ease-out;

  svg {
    filter: none;
  }
`;

export const HeroLabel = styled.span`
  margin-top: 4px;
  padding: 4px 10px;
  border-radius: ${radius.pill};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.16);
`;

export const Title = styled.h2`
  font-size: 23px;
  font-weight: 800;
  line-height: 1.25;
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 250px;
  padding: 22px 24px 8px;
  overflow-y: auto;
  animation: ${slideIn} 0.25s ease-out;

  @media (max-width: 480px) {
    padding: 18px 16px 8px;
  }
`;

export const Lead = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: ${colors.muted};
`;

export const Point = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
`;

export const PointIcon = styled.span`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: ${radius.sm};
  color: ${colors.brandDeep};
  background: ${colors.brandSoft};
`;

export const Course = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: ${radius.md};
  border: 2px solid ${colors.brand};
  background: ${colors.brandSoft};

  b {
    font-size: 16px;
    font-weight: 800;
  }

  span {
    padding: 4px 10px;
    border-radius: ${radius.pill};
    font-size: 13px;
    font-weight: 800;
    color: #FFFFFF;
    background: ${colors.brand};
  }
`;

export const Note = styled.p`
  padding: 12px 14px;
  border-radius: ${radius.md};
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.ink};
  background: ${colors.amberSoft};
`;

export const Path = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;

  & > span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: ${colors.muted};
  }
`;

export const Crumb = styled.b`
  padding: 6px 10px;
  border-radius: ${radius.sm};
  font-size: 13px;
  font-weight: 800;
  color: ${(props) => (props.$last ? "#FFFFFF" : colors.brandDeep)};
  background: ${(props) => (props.$last ? colors.brand : colors.brandSoft)};
`;

export const Keys = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

export const Key = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${colors.muted};

  kbd {
    min-width: 34px;
    padding: 4px 8px;
    border-radius: 8px;
    border: 1px solid ${colors.line};
    border-bottom-width: 3px;
    font-family: ${font.text};
    font-size: 13px;
    font-weight: 800;
    text-align: center;
    color: ${colors.ink};
    background: ${colors.surface};
  }
`;

export const Mock = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 10px 12px;
  border-radius: ${radius.md};
  border: 1px dashed ${colors.brand};
  font-size: 14px;
  color: ${colors.muted};
`;

export const MockSegment = styled.span`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: ${(props) => (props.$active ? "#FFFFFF" : colors.brandDeep)};
  background: ${(props) => (props.$active ? colors.brand : colors.brandSoft)};
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: 16px 24px 22px;

  @media (max-width: 480px) {
    padding: 14px 16px 18px;
  }
`;

export const Dots = styled.div`
  display: flex;
  gap: 6px;
`;

export const Dot = styled.button`
  width: ${(props) => (props.$active ? "22px" : "8px")};
  height: 8px;
  padding: 0;
  border: none;
  border-radius: ${radius.pill};
  background: ${(props) => (props.$active ? colors.brand : colors.line)};
  cursor: pointer;
  transition: width 0.2s ease, background 0.2s ease;
`;

export const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

export const Primary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: ${radius.md};
  font-family: ${font.text};
  font-size: 15px;
  font-weight: 800;
  color: #FFFFFF;
  background: ${colors.brand};
  box-shadow: ${shadow.button(colors.brandDeep)};
  cursor: pointer;

  &:active {
    transform: translateY(2px);
  }
`;

export const Secondary = styled(Primary)`
  padding: 12px 14px;
  color: ${colors.brandDeep};
  background: ${colors.brandSoft};
  box-shadow: none;
`;
